import axios from '@/core/axios';
import { CollectionList } from '../collections/collections.types';
import { OrderSparePart, OrderSparePartsListOptions, OrderSparePartsStatusResponse } from './order-spare-parts.types';

export class OrderSparePartsService {
  list(opts: OrderSparePartsListOptions = {}): Promise<CollectionList<'order-spare-parts'>> {
    const { filters, pagination } = opts;
    // Remap 'fulltext' (sent by table store) to 'text' (expected by API)
    const { fulltext, ...restFilters } = filters || {};
    // Backend expects 'per_page' instead of 'limit' for this collection
    const { limit, ...restPagination } = pagination || {};
    const paginationParams = { ...restPagination, ...(limit ? { per_page: limit } : {}) };
    return axios
      .get<any>('/order-spare-parts', {
        params: { ...restFilters, ...paginationParams, ...(fulltext ? { text: fulltext } : {}) }
      })
      .then(resp => {
        const data = resp?.data;
        if (!data) return { count: 0, values: [] };
        // Normalize the API payload to the { count, values } shape expected by the table store
        const values = data.values ?? data.rows ?? data.data ?? (Array.isArray(data) ? data : []);
        const count =
          data.count ?? data.total ?? data.total_count ?? data.meta?.total ?? data.meta?.total_count ?? values.length;
        return { count, values };
      });
  }

  get(id: number, deviceId?: number): Promise<OrderSparePart> {
    return axios
      .get<OrderSparePart>(`/order-spare-parts/${id}`, {
        params: deviceId ? { device_id: deviceId } : {}
      })
      .then(resp => resp?.data);
  }

  update(id: number, data: Partial<OrderSparePart>): Promise<OrderSparePart> {
    return axios.put<OrderSparePart>(`/order-spare-parts/${id}`, data).then(resp => resp?.data);
  }

  getByDevice(deviceId: number): Promise<OrderSparePart[]> {
    return axios
      .get<{ values: OrderSparePart[] }>(`/order-spare-parts/by-device/${deviceId}`)
      .then(resp => resp?.data?.values);
  }

  getStatuses(): Promise<OrderSparePartsStatusResponse> {
    return axios.get<OrderSparePartsStatusResponse>('/order-spare-parts/statuses').then(resp => resp?.data);
  }

  getByBac(bac: string, serialNo?: string): Promise<OrderSparePart> {
    return axios
      .get<OrderSparePart>(`/order-spare-parts/by-bac/${bac}`, {
        params: serialNo ? { serial_no: serialNo } : {}
      })
      .then(resp => resp?.data);
  }

  generateOfDolibarr(ospId: number): Promise<OrderSparePart> {
    return axios.post<OrderSparePart>(`/order-spare-parts/${ospId}/generate-of-dolibarr`).then(resp => resp?.data);
  }

  finish(id: number, bac?: string): Promise<OrderSparePart> {
    return axios.put<OrderSparePart>(`/order-spare-parts/${id}/finish`, bac ? { bac } : {}).then(resp => resp?.data);
  }

  // Mark one or more spare parts of an order as out of stock (hors_stock)
  markOutOfStock(id: number, sparePartIds: number[]): Promise<OrderSparePart> {
    return axios
      .put<OrderSparePart>(`/order-spare-parts/${id}/out-stock`, { spare_part_ids: sparePartIds })
      .then(resp => resp?.data);
  }

  // Mark order spare parts as in stock
  putInStock(id: number): Promise<OrderSparePart> {
    return axios.put<OrderSparePart>(`/order-spare-parts/${id}/put-in-stock`).then(resp => resp?.data);
  }
}

export const orderSparePartsService = new OrderSparePartsService();
